import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/dr-is-in.png";
import "./AdminOrgRoster.css";
import { AURA_GEOGRAPHY } from "../../data/auraGeography";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-backend-v12.onrender.com").replace(/\/+$/, "");

type Org = {
  id: string;
  org_id?: string;
  school_id?: string;
  org_code: string;
  org_name: string;
  is_active?: boolean | number | string;
  created_at?: string;
  contact_name?: string | null;
  contact_title?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  region?: string | null;
  district?: string | null;
  notes?: string | null;
  school_pin_set?: boolean | number | string;
  school_pin_last4?: string | null;
  school_pin_set_at?: string | null;
  school_admin_pin?: string | null;
  raw_school_pin?: string | null;
};

type Student = {
  id: string;
  student_pk?: string;
  student_id: string;
  name?: string | null;
  email?: string | null;
  grade?: string | null;
  is_active?: boolean | number | string;
  pin_set?: boolean | number | string;
  pin_last4?: string | null;
  raw_pin?: string | null;
  pin?: string | null;
  student_pin?: string | null;
  created_at?: string;
};

type PtAccount = {
  id: string;
  pt_code: string;
  pt_role?: string;
  pt_name?: string | null;
  pt_email?: string | null;
  is_active?: boolean | number | string;
  pin_set?: boolean | number | string;
  pin_last4?: string | null;
  raw_pin?: string | null;
  pin?: string | null;
  pt_pin?: string | null;
  created_at?: string;
};

type Submission = {
  id: number;
  org_id: string;
  org_code?: string;
  status?: string;
  created_at?: string;
  submitted_at?: string;
  approved_at?: string;
  rejected_at?: string;
  row_count?: number;
  pin_report_ready?: boolean | number | string;
};

type SubmissionRow = {
  row_num?: number;
  record_type?: string;
  student_id?: string;
  student_first?: string;
  student_last?: string;
  student_grade?: string;
  pt_code?: string;
  pt_role?: string;
  pt_name?: string;
  pt_email?: string;
  raw_pin?: string;
  pin?: string;
  student_pin?: string;
  pt_pin?: string;
  error_text?: string;
};

function clean(v: any) {
  return String(v ?? "").trim();
}

function truthyActive(v: any): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "1" || s === "true" || s === "active" || s === "approved" || s === "yes";
  }
  return false;
}

function truthy(v: any): boolean {
  return truthyActive(v);
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.map(clean).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function getAllRegions(): string[] {
  return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.map((r) => r.name)));
}

function getCountriesByRegion(region: string): string[] {
  if (!region) return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.flatMap((r) => r.countries)));
  for (const continent of AURA_GEOGRAPHY) {
    for (const reg of continent.regions) {
      if (reg.name === region) return uniqueSorted(reg.countries);
    }
  }
  return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.flatMap((r) => r.countries)));
}

function getRegionForCountry(country: string): string {
  const target = clean(country);
  if (!target) return "";
  for (const continent of AURA_GEOGRAPHY) {
    for (const region of continent.regions) {
      if (region.countries.includes(target)) return region.name;
    }
  }
  return "";
}

function getAdminToken() {
  return (
    localStorage.getItem("admin_jwt") ||
    localStorage.getItem("admin_access_token") ||
    localStorage.getItem("admin_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("admin_jwt") ||
    sessionStorage.getItem("admin_access_token") ||
    sessionStorage.getItem("admin_token") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

function buildHeaders(json = true): HeadersInit {
  const token = getAdminToken();
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function apiJson<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...buildHeaders(!(init.body instanceof FormData)), ...(init.headers || {}) },
    credentials: "include",
  });

  const text = await res.text();
  let data: any = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { detail: text };
  }

  if (!res.ok) {
    const detail = data?.detail || data?.message || res.statusText || "API request failed";
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data as T;
}

async function apiBlob(path: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: buildHeaders(false),
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || "Download failed");
  }
  return res.blob();
}

async function apiJsonFirst<T = any>(paths: string[], init: RequestInit = {}): Promise<T> {
  let lastErr: any = null;
  for (const path of paths) {
    try {
      return await apiJson<T>(path, init);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("All API routes failed");
}

function normalizeOrg(o: any): Org {
  const id = String(o?.id ?? o?.org_id ?? o?.organization_id ?? o?.school_id ?? "");
  const orgName = clean(o?.org_name ?? o?.name ?? o?.school_name ?? o?.organization_name);
  const pinPlain = clean(o?.school_admin_pin ?? o?.raw_school_pin ?? o?.school_pin);
  const last4 = clean(o?.school_pin_last4 ?? o?.school_admin_pin_last4);
  const pinSet = o?.school_pin_set ?? o?.school_admin_pin_set ?? Boolean(pinPlain || last4);

  return {
    ...o,
    id,
    org_id: String(o?.org_id ?? o?.id ?? id),
    school_id: o?.school_id ? String(o.school_id) : undefined,
    org_code: clean(o?.org_code ?? o?.code),
    org_name: orgName,
    is_active: o?.is_active ?? o?.active ?? o?.status ?? true,
    created_at: o?.created_at,
    contact_name: clean(o?.contact_name ?? o?.admin_name) || null,
    contact_title: clean(o?.contact_title ?? o?.admin_title) || null,
    contact_email: clean(o?.contact_email ?? o?.admin_email) || null,
    contact_phone: clean(o?.contact_phone ?? o?.admin_phone) || null,
    address1: clean(o?.address1 ?? o?.address ?? o?.street1) || null,
    address2: clean(o?.address2 ?? o?.street2) || null,
    city: clean(o?.city) || null,
    state: clean(o?.state) || null,
    zip: clean(o?.zip ?? o?.zipcode ?? o?.postal_code) || null,
    country: clean(o?.country) || null,
    region: clean(o?.region) || null,
    district: clean(o?.district ?? o?.school_district ?? o?.district_name) || null,
    notes: clean(o?.notes) || null,
    school_pin_set: pinSet,
    school_pin_last4: last4 || null,
    school_pin_set_at: clean(o?.school_pin_set_at ?? o?.school_admin_pin_set_at) || null,
    school_admin_pin: pinPlain || null,
    raw_school_pin: pinPlain || null,
  };
}

function normalizeStudent(s: any): Student {
  const pk = String(s?.student_pk ?? s?.id ?? "");
  const displayId = clean(s?.student_id ?? s?.login_id ?? s?.display_id ?? pk);
  return {
    ...s,
    id: pk,
    student_pk: pk,
    student_id: displayId,
    name: clean(s?.name ?? s?.display_name ?? s?.student_name ?? s?.full_name) || null,
    email: clean(s?.email) || null,
    grade: clean(s?.grade) || null,
    is_active: s?.is_active ?? s?.active ?? s?.status ?? true,
    pin_set: s?.pin_set ?? s?.has_pin ?? s?.pin_last4 ?? s?.student_pin ?? s?.raw_pin ?? 0,
    pin_last4: clean(s?.pin_last4) || null,
    raw_pin: clean(s?.raw_pin ?? s?.pin ?? s?.student_pin) || null,
    pin: clean(s?.pin ?? s?.student_pin ?? s?.raw_pin) || null,
    student_pin: clean(s?.student_pin ?? s?.pin ?? s?.raw_pin) || null,
    created_at: s?.created_at ?? undefined,
  };
}

function normalizePtAccount(p: any): PtAccount {
  return {
    ...p,
    id: String(p?.id ?? p?.pt_id ?? p?.account_id ?? ""),
    pt_code: clean(p?.pt_code ?? p?.code ?? p?.login_id ?? p?.username ?? p?.email),
    pt_role: clean(p?.pt_role ?? p?.role),
    pt_name: clean(p?.pt_name ?? p?.name ?? p?.display_name) || null,
    pt_email: clean(p?.pt_email ?? p?.email) || null,
    is_active: p?.is_active ?? p?.active ?? p?.status ?? true,
    pin_set: p?.pin_set ?? p?.pin_last4 ?? p?.has_pin ?? p?.pt_pin ?? p?.raw_pin ?? 0,
    pin_last4: clean(p?.pin_last4) || null,
    raw_pin: clean(p?.raw_pin ?? p?.pin ?? p?.pt_pin) || null,
    pin: clean(p?.pin ?? p?.pt_pin ?? p?.raw_pin) || null,
    pt_pin: clean(p?.pt_pin ?? p?.pin ?? p?.raw_pin) || null,
    created_at: p?.created_at ?? undefined,
  };
}

function normalizeSubmission(s: any): Submission {
  return {
    id: Number(s?.id ?? s?.submission_id ?? 0),
    org_id: String(s?.org_id ?? ""),
    org_code: clean(s?.org_code),
    status: clean(s?.status),
    created_at: clean(s?.created_at),
    submitted_at: clean(s?.submitted_at),
    approved_at: clean(s?.approved_at),
    rejected_at: clean(s?.rejected_at),
    row_count: Number(s?.row_count ?? s?.rows_count ?? 0),
    pin_report_ready: s?.pin_report_ready ?? false,
  };
}

function issuedKeyFromSubmissionRow(r: SubmissionRow) {
  const type = String(r.record_type || "").toLowerCase();
  if (type === "student") return `student:${String(r.student_id || "").trim()}`;
  if (type === "pt") return `pt:${String(r.pt_code || "").trim()}`;
  return "";
}

function studentIssuedKey(studentId: string) {
  return `student:${String(studentId || "").trim()}`;
}

function ptIssuedKey(ptCode: string) {
  return `pt:${String(ptCode || "").trim()}`;
}

function formatPinDisplay(fullPin?: string | null, last4?: string | null, pinSet?: any) {
  const pin = String(fullPin || "").trim();
  if (pin && /^\d{6}$/.test(pin)) return pin;
  const l4 = String(last4 || pin || "").trim();
  if (l4) return `PIN on file (${l4.slice(-4)})`;
  return truthy(pinSet) ? "PIN set" : "—";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function trackRosterEvent(_eventType: string, _metadata: Record<string, unknown> = {}) {
  // Operational data now goes through FastAPI/SQLite. Analytics tracking is intentionally non-blocking.
}

export default function AdminOrgRoster() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgQuery, setOrgQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);

  const [students, setStudents] = useState<Student[]>([]);
  const [studentQuery, setStudentQuery] = useState("");

  const [pts, setPts] = useState<PtAccount[]>([]);
  const [ptQuery, setPtQuery] = useState("");

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [submissionRows, setSubmissionRows] = useState<SubmissionRow[]>([]);
  const [issuedPinsByKey, setIssuedPinsByKey] = useState<Record<string, string>>({});
  const [submissionBusy, setSubmissionBusy] = useState(false);

  const [pendingOnly, setPendingOnly] = useState(false);

  const [contactDraft, setContactDraft] = useState<Partial<Org>>({});
  const [contactBusy, setContactBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const didInitRef = useRef(false);
  const selectedOrgIdRef = useRef<string | null>(null);

  const regionOptions = useMemo(() => getAllRegions(), []);
  const countryOptions = useMemo(() => getCountriesByRegion(String(contactDraft.region ?? "")), [contactDraft.region]);

  useEffect(() => {
    selectedOrgIdRef.current = selectedOrg?.id ?? null;
    if (selectedOrg) {
      setContactDraft({
        contact_name: selectedOrg.contact_name ?? "",
        contact_title: selectedOrg.contact_title ?? "",
        contact_email: selectedOrg.contact_email ?? "",
        contact_phone: selectedOrg.contact_phone ?? "",
        address1: selectedOrg.address1 ?? "",
        address2: selectedOrg.address2 ?? "",
        city: selectedOrg.city ?? "",
        state: selectedOrg.state ?? "",
        zip: selectedOrg.zip ?? "",
        country: selectedOrg.country ?? "",
        region: selectedOrg.region ?? "",
        district: selectedOrg.district ?? "",
        notes: selectedOrg.notes ?? "",
      });
    } else {
      setContactDraft({});
    }
  }, [selectedOrg]);

  const handleLogout = useCallback(() => {
    ["admin_jwt", "admin_token", "admin_access_token", "access_token", "token", "role", "doctor_id"].forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    navigate("/");
  }, [navigate]);

  const fetchOrgDetail = useCallback(async (orgId: string) => {
    try {
      const data = await apiJson<any>(`/admin/organizations/${encodeURIComponent(orgId)}`);
      return normalizeOrg(data.organization || data.item || data.org || data);
    } catch {
      return null;
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const data = await apiJson<any>(`/admin/organizations`);
      const raw = data.items || data.organizations || data.rows || [];
      const normalized = raw.map(normalizeOrg).filter((o: Org) => o.id && o.org_name);
      setOrgs(normalized);

      const prevId = selectedOrgIdRef.current;
      if (prevId) {
        const fresh = await fetchOrgDetail(prevId);
        const updated = fresh || normalized.find((x: Org) => x.id === prevId);
        if (updated) setSelectedOrg(updated);
        else {
          setSelectedOrg(null);
          setStudents([]);
          setPts([]);
          setSubmissions([]);
          setSelectedSubmission(null);
          setSubmissionRows([]);
          setIssuedPinsByKey({});
        }
      }
    } catch (e: any) {
      setError(String(e?.message || "Failed to load organizations from backend."));
    } finally {
      setLoading(false);
    }
  }, [fetchOrgDetail]);

  const fetchStudentsOnly = useCallback(async (orgId?: string | null) => {
    if (!orgId) {
      setStudents([]);
      return;
    }
    try {
      const data = await apiJson<any>(`/admin/organizations/${encodeURIComponent(orgId)}/students`);
      const rows = data.items || data.students || data.rows || [];
      setStudents(rows.map(normalizeStudent).filter((s: Student) => !!s.id));
    } catch (e) {
      console.error("[AOR] fetchStudentsOnly failed", e);
      setStudents([]);
    }
  }, []);

  const fetchPtsOnly = useCallback(async (orgId?: string | null) => {
    if (!orgId) {
      setPts([]);
      return;
    }
    try {
      const data = await apiJsonFirst<any>([
        `/admin/organizations/${encodeURIComponent(orgId)}/pt-accounts`,
        `/admin/organizations/${encodeURIComponent(orgId)}/pt`,
        `/admin/organizations/${encodeURIComponent(orgId)}/parents-teachers`,
        `/admin/pt-accounts?org_id=${encodeURIComponent(orgId)}`,
      ]);
      const rows = data.items || data.pt_accounts || data.accounts || data.parent_teachers || data.pts || data.rows || [];
      setPts(rows.map(normalizePtAccount).filter((p: PtAccount) => p.id && !!p.pt_code));
    } catch (e) {
      console.error("[AOR] fetchPtsOnly failed", e);
      setPts([]);
    }
  }, []);

  const fetchSubmissionsOnly = useCallback(async (orgId?: string | null) => {
    if (!orgId) {
      setSubmissions([]);
      return;
    }
    try {
      const data = await apiJsonFirst<any>([
        `/admin/organizations/${encodeURIComponent(orgId)}/roster-submissions`,
        `/admin/roster-submissions?org_id=${encodeURIComponent(orgId)}`,
        `/school/admin/organizations/${encodeURIComponent(orgId)}/roster-submissions`,
      ]);
      const rows = data.items || data.submissions || data.rows || [];
      setSubmissions(rows.map(normalizeSubmission).filter((s: Submission) => s.id));
    } catch {
      setSubmissions([]);
    }
  }, []);

  const fetchOrgRoster = useCallback(async (org: Org) => {
    const orgId = org.id;
    // Version 1.2: load the live roster AND School Admin enrollment submissions.
    await Promise.all([
      fetchStudentsOnly(orgId),
      fetchPtsOnly(orgId),
      fetchSubmissionsOnly(orgId),
    ]);
  }, [fetchPtsOnly, fetchStudentsOnly, fetchSubmissionsOnly]);

  const fetchSubmissionRows = useCallback(async (sub?: Submission | null) => {
    const target = sub || selectedSubmission;
    if (!target) {
      setSubmissionRows([]);
      return;
    }
    setSubmissionBusy(true);
    try {
      const data = await apiJsonFirst<any>([
        `/admin/roster-submissions/${target.id}/rows`,
        `/admin/roster-submission/${target.id}/rows`,
        `/admin/roster-submissions/${target.id}/row`,
        `/school/admin/roster-submissions/${target.id}/rows`,
      ]);
      setSubmissionRows(data.rows || data.items || []);
    } catch (e: any) {
      setError(String(e?.message || "Failed to load submission rows."));
      setSubmissionRows([]);
    } finally {
      setSubmissionBusy(false);
    }
  }, [selectedSubmission]);

  const refreshSelectedOrgData = useCallback(async (org?: Org | null) => {
    await fetchOrgs();
    const target = org || selectedOrg;
    if (target) await fetchOrgRoster(target);
  }, [fetchOrgs, fetchOrgRoster, selectedOrg]);

  const viewSubmission = useCallback(async (sub: Submission) => {
    setSelectedSubmission(sub);
    await fetchSubmissionRows(sub);
  }, [fetchSubmissionRows]);

  const approveSubmission = useCallback(async () => {
    if (!selectedSubmission || !selectedOrg) return;
    setSubmissionBusy(true);
    setError("");
    try {
      const data = await apiJsonFirst<any>([
        `/admin/roster-submissions/${selectedSubmission.id}/approve`,
        `/school/admin/roster-submissions/${selectedSubmission.id}/approve`,
      ], { method: "POST" });

      const issued: Record<string, string> = {};
      for (const r of data.issued_rows || []) {
        if (r.record_type === "student" && r.student_id && r.pin) issued[studentIssuedKey(r.student_id)] = r.pin;
        if (r.record_type === "pt" && r.pt_code && r.pin) issued[ptIssuedKey(r.pt_code)] = r.pin;
      }
      setIssuedPinsByKey((prev) => ({ ...prev, ...issued }));
      await fetchSubmissionsOnly(selectedOrg.id);
      await fetchOrgRoster(selectedOrg);
      window.alert("Submission approved. Copy any displayed PINs now or download the authorized PIN CSV.");
    } catch (e: any) {
      setError(String(e?.message || "Failed to approve submission."));
    } finally {
      setSubmissionBusy(false);
    }
  }, [fetchOrgRoster, fetchSubmissionsOnly, selectedOrg, selectedSubmission]);

  const rejectSubmission = useCallback(async () => {
    if (!selectedSubmission || !selectedOrg) return;
    const reason = window.prompt("Reason for rejection:", "Needs correction") || "Rejected by admin";
    setSubmissionBusy(true);
    setError("");
    try {
      await apiJsonFirst<any>([
        `/admin/roster-submissions/${selectedSubmission.id}/reject`,
        `/school/admin/roster-submissions/${selectedSubmission.id}/reject`,
      ], { method: "POST", body: JSON.stringify({ reason }) });
      await fetchSubmissionsOnly(selectedOrg.id);
    } catch (e: any) {
      setError(String(e?.message || "Failed to reject submission."));
    } finally {
      setSubmissionBusy(false);
    }
  }, [fetchSubmissionsOnly, selectedOrg, selectedSubmission]);

  const downloadSubmissionCsv = useCallback(async (sub: Submission) => {
    if (!selectedOrg) return;
    try {
      const blob = await apiBlob(`/school/pin-report.csv?org_id=${encodeURIComponent(selectedOrg.id)}&submission_id=${encodeURIComponent(String(sub.id))}`);
      downloadBlob(blob, `authorized_pins_org_${selectedOrg.id}_submission_${sub.id}.csv`);
    } catch (e: any) {
      setError(String(e?.message || "Failed to download authorized PIN CSV."));
    }
  }, [selectedOrg]);

  const onSelectOrg = useCallback(async (org: Org) => {
    setSelectedOrg(org);
    setStudents([]);
    setPts([]);
    setSubmissions([]);
    setSelectedSubmission(null);
    setSubmissionRows([]);
    setIssuedPinsByKey({});
    setError("");

    try {
      const fresh = await fetchOrgDetail(org.id);
      const target = fresh || org;
      setSelectedOrg(target);
      await fetchOrgRoster(target);
    } catch (e: any) {
      setError(String(e?.message || "Failed to load organization roster from backend."));
      setStudents([]);
      setPts([]);
      setSubmissions([]);
    }
  }, [fetchOrgDetail, fetchOrgRoster]);

  const setOrgActive = useCallback(async (orgId: string, isActive: boolean) => {
    setError("");
    setLoading(true);
    try {
      let approvalData: any = null;

      if (isActive) {
        approvalData = await apiJson<any>(`/admin/organizations/${encodeURIComponent(orgId)}/approve`, {
          method: "POST",
        });
      } else {
        await apiJson<any>(`/admin/organizations/${encodeURIComponent(orgId)}`, {
          method: "PATCH",
          body: JSON.stringify({ is_active: false, active: false }),
        });
      }

      await trackRosterEvent("aura_signal_generated", {
        action: isActive ? "organization_approved" : "organization_deactivated",
        organization_id: orgId,
        active: isActive,
      });

      const fresh = await fetchOrgDetail(orgId);
      if (fresh) {
        const issuedPin = clean(
          approvalData?.pin ||
          approvalData?.school_admin_pin ||
          approvalData?.raw_school_pin
        );

        const withApprovalPin = issuedPin
          ? {
              ...fresh,
              school_admin_pin: issuedPin,
              raw_school_pin: issuedPin,
              school_pin_set: true,
              school_pin_last4: issuedPin.slice(-4),
              school_pin_set_at: clean(approvalData?.pin_set_at) || fresh.school_pin_set_at,
            }
          : fresh;

        setSelectedOrg(withApprovalPin);

        if (isActive && issuedPin) {
          window.prompt("SCHOOL APPROVED — copy the School Admin PIN now:", issuedPin);
        } else if (isActive) {
          window.alert("School approved.");
        }

        await refreshSelectedOrgData(withApprovalPin);
      } else {
        await refreshSelectedOrgData(selectedOrg);
      }
    } catch (e: any) {
      setError(String(e?.message || (isActive ? "Failed to approve organization." : "Failed to deactivate organization.")));
    } finally {
      setLoading(false);
    }
  }, [fetchOrgDetail, refreshSelectedOrgData, selectedOrg]);

  const reissueStudentPin = useCallback(async (studentPk: string) => {
    if (!selectedOrg) return;
    setError("");
    setLoading(true);
    try {
      const student = students.find((s) => String(s.student_pk ?? s.id) === String(studentPk));
      const data = await apiJson<any>(`/school/reissue-pin?org_id=${encodeURIComponent(selectedOrg.id)}`, {
        method: "POST",
        body: JSON.stringify({ org_id: selectedOrg.id, org_code: selectedOrg.org_code, student_pk: Number(studentPk) || 0, student_id: student?.student_id || "" }),
      });
      const newPin = clean(data.pin || data.new_pin || data.student_pin);
      if (newPin) {
        window.prompt("NEW STUDENT PIN (copy now):", newPin);
        if (student?.student_id) setIssuedPinsByKey((prev) => ({ ...prev, [studentIssuedKey(student.student_id)]: newPin }));
      }
      await fetchStudentsOnly(selectedOrg.id);
    } catch (e: any) {
      setError(String(e?.message || "Failed to reissue student PIN."));
    } finally {
      setLoading(false);
    }
  }, [fetchStudentsOnly, selectedOrg, students]);

  const reissuePtPin = useCallback(async (ptPk: string) => {
    if (!selectedOrg) return;
    setError("");
    setLoading(true);
    try {
      const pt = pts.find((p) => String(p.id) === String(ptPk));
      const data = await apiJson<any>(`/school/reissue-pt-pin?org_id=${encodeURIComponent(selectedOrg.id)}`, {
        method: "POST",
        body: JSON.stringify({ org_id: selectedOrg.id, org_code: selectedOrg.org_code, pt_id: Number(ptPk) || 0, pt_code: pt?.pt_code || "" }),
      });
      const newPin = clean(data.pin || data.new_pin || data.pt_pin);
      if (newPin) {
        window.prompt("NEW PARENT/TEACHER PIN (copy now):", newPin);
        if (pt?.pt_code) setIssuedPinsByKey((prev) => ({ ...prev, [ptIssuedKey(pt.pt_code)]: newPin }));
      }
      await fetchPtsOnly(selectedOrg.id);
    } catch (e: any) {
      setError(String(e?.message || "Failed to reissue parent/teacher PIN."));
    } finally {
      setLoading(false);
    }
  }, [fetchPtsOnly, selectedOrg, pts]);

  const setStudentActive = useCallback(async (_studentPk: string, _isActive: boolean) => {
    setError("Student activation needs a backend admin endpoint. PIN and roster loading are now live; this action is intentionally blocked instead of fake-updating Supabase.");
  }, []);

  const setPtActive = useCallback(async (_ptPk: string, _isActive: boolean) => {
    setError("Parent/teacher activation needs a backend admin endpoint. PIN and roster loading are now live; this action is intentionally blocked instead of fake-updating Supabase.");
  }, []);

  const setAllStudentsActiveForOrg = useCallback(async (_orgId: string, _isActive: boolean) => {
    setError("Bulk student activation needs a backend admin endpoint. This button is blocked until that route is added, so it cannot silently update the wrong database.");
  }, []);

  const issueSchoolPin = useCallback(async (orgId: string, reissue: boolean) => {
    if (!selectedOrg) return;
    setError("");
    setLoading(true);
    try {
      const data = await apiJsonFirst<any>([
        `/admin/organizations/${encodeURIComponent(orgId)}/issue-school-admin-pin`,
        `/admin/organizations/${encodeURIComponent(orgId)}/issue_school_admin_pin`,
        `/admin/organizations/${encodeURIComponent(orgId)}/school-admin-pin`,
        `/admin_school_admin_pin`,
      ], { method: "POST", body: JSON.stringify({ org_id: orgId }) });

      const newPin = clean(data.pin || data.school_admin_pin || data.raw_school_pin);
      if (!newPin) throw new Error("Backend did not return the new PIN.");
      window.prompt(`${reissue ? "NEW" : "SCHOOL ADMIN"} PIN for ${selectedOrg.org_code || ""} (copy now):`, newPin);
      const fresh = await fetchOrgDetail(orgId);
      if (fresh) setSelectedOrg({ ...fresh, school_admin_pin: newPin, raw_school_pin: newPin, school_pin_set: true, school_pin_last4: newPin.slice(-4), school_pin_set_at: clean(data.pin_set_at) || fresh.school_pin_set_at });
      await fetchOrgs();
      await trackRosterEvent("aura_signal_generated", { action: reissue ? "school_admin_pin_reissued" : "school_admin_pin_issued", organization_id: orgId });
    } catch (e: any) {
      setError(String(e?.message || "Failed to issue school admin PIN."));
    } finally {
      setLoading(false);
    }
  }, [fetchOrgDetail, fetchOrgs, selectedOrg]);

  const saveOrgContact = useCallback(async () => {
    if (!selectedOrg) return;
    setContactBusy(true);
    setError("");
    try {
      await apiJson<any>(`/admin/organizations/${encodeURIComponent(selectedOrg.id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          contact_name: clean(contactDraft.contact_name),
          contact_title: clean(contactDraft.contact_title),
          contact_email: clean(contactDraft.contact_email),
          contact_phone: clean(contactDraft.contact_phone),
          address1: clean(contactDraft.address1),
          address2: clean(contactDraft.address2),
          city: clean(contactDraft.city),
          state: clean(contactDraft.state),
          zip: clean(contactDraft.zip),
          country: clean(contactDraft.country),
          region: clean(contactDraft.region),
          district: clean(contactDraft.district),
          notes: clean(contactDraft.notes),
        }),
      });
      const fresh = await fetchOrgDetail(selectedOrg.id);
      if (fresh) setSelectedOrg(fresh);
      await fetchOrgs();
      await fetchOrgRoster(fresh || selectedOrg);
    } catch (e: any) {
      setError(String(e?.message || "Failed to save contact info."));
    } finally {
      setContactBusy(false);
    }
  }, [contactDraft, fetchOrgDetail, fetchOrgRoster, fetchOrgs, selectedOrg]);

  const deleteSelectedOrg = useCallback(async () => {
    setDeleteBusy(false);
    setError("Delete Organization is disabled until a backend delete route is added. This prevents deleting from Supabase while the live app is using SQLite.");
  }, []);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchOrgs();
  }, [fetchOrgs]);

  const pendingCount = useMemo(() => orgs.filter((o) => !truthyActive(o.is_active)).length, [orgs]);

  const filteredOrgs = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    return orgs
      .filter((o) => (!pendingOnly ? true : !truthyActive(o.is_active)))
      .filter((o) => {
        if (!q) return true;
        return [o.org_name, o.org_code, o.contact_name, o.contact_email, o.country, o.region, o.district]
          .map((x) => String(x || "").toLowerCase())
          .some((x) => x.includes(q));
      });
  }, [orgs, orgQuery, pendingOnly]);

  const filteredStudents = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => [s.student_id, s.name, s.email, s.grade].map((x) => String(x || "").toLowerCase()).some((x) => x.includes(q)));
  }, [students, studentQuery]);

  const filteredPts = useMemo(() => {
    const q = ptQuery.trim().toLowerCase();
    if (!q) return pts;
    return pts.filter((p) => [p.pt_code, p.pt_name, p.pt_email, p.pt_role].map((x) => String(x || "").toLowerCase()).some((x) => x.includes(q)));
  }, [pts, ptQuery]);

  const schoolPinSet = truthy(selectedOrg?.school_pin_set);

  return (
    <div className="aor-page">
      <div className="aor-card">
        <div className="aor-topbrand">
          <img className="aor-logo" src={logo} alt="Doctor Is In" />
          <div className="aor-topbrand-text">
            <div className="aor-topbrand-name">Doctor Is In</div>
            <div className="aor-topbrand-tagline">Advisor AI with a human touch.</div>
          </div>
        </div>

        <div className="aor-header">
          <div>
            <div className="aor-title">Admin Organization Roster</div>
            <div className="aor-subtitle">Manage organizations, school admin access, students, parents/teachers, activation, and PIN access.</div>
          </div>

          <div className="aor-header-actions">
            <button className="aor-btn" type="button" onClick={fetchOrgs} disabled={loading || submissionBusy}>Refresh</button>
            <button className="aor-btn" type="button" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        {error ? <div className="aor-error aor-pre">{error}</div> : null}

        <div className="aor-grid">
          <div className="aor-panel">
            <div className="aor-panel-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span>Organizations</span>
              <span className="aor-pill bad" title="Organizations not yet approved (inactive)" style={{ whiteSpace: "nowrap" }}>Pending: {pendingCount}</span>
            </div>

            <input className="aor-input" value={orgQuery} onChange={(e) => setOrgQuery(e.target.value)} placeholder="Search org name, code, contact, email, country, region, or district…" />

            <label className="aor-checkline" style={{ marginTop: 10 }}>
              <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
              <span>Pending approvals only</span>
            </label>

            <div className="aor-list">
              {filteredOrgs.length === 0 ? (
                <div className="aor-empty">{pendingOnly ? "No pending approvals." : "No organizations found."}</div>
              ) : (
                filteredOrgs.map((o) => {
                  const active = truthyActive(o.is_active);
                  const selected = selectedOrg?.id === o.id;
                  return (
                    <button key={o.id} type="button" className={`aor-list-item ${selected ? "selected" : ""}`} onClick={() => onSelectOrg(o)}>
                      <div className="aor-list-main">
                        <div className="aor-list-name">{o.org_name}</div>
                        <div className="aor-list-code">{o.org_code}</div>
                        {o.contact_name || o.contact_email ? <div className="aor-list-code">{[o.contact_name, o.contact_email].filter(Boolean).join(" • ")}</div> : null}
                        {o.country || o.region || o.district ? <div className="aor-list-code">{[o.country, o.region, o.district].filter(Boolean).join(" • ")}</div> : null}
                      </div>
                      <span className={`aor-pill ${active ? "ok" : "bad"}`}>{active ? "Active" : "Pending Approval"}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="aor-panel">
            <div className="aor-panel-title">Organization Roster</div>

            {!selectedOrg ? (
              <div className="aor-empty">Select an organization on the left.</div>
            ) : (
              <>
                <div className="aor-org-box">
                  <div className="aor-org-row" style={{ alignItems: "flex-start" }}>
                    <div>
                      <div className="aor-org-name">{selectedOrg.org_name}</div>
                      <div className="aor-org-meta">Org Code: {selectedOrg.org_code}</div>

                      <div className="aor-org-meta" style={{ marginTop: 8 }}>
                        <b>School Admin PIN:</b>{" "}
                        {selectedOrg.school_admin_pin || selectedOrg.raw_school_pin ? (
                          <>{selectedOrg.school_admin_pin || selectedOrg.raw_school_pin}{selectedOrg.school_pin_set_at ? <span className="muted"> ({selectedOrg.school_pin_set_at})</span> : null}</>
                        ) : schoolPinSet ? (
                          <>PIN set{selectedOrg.school_pin_last4 ? ` (${selectedOrg.school_pin_last4})` : ""}{selectedOrg.school_pin_set_at ? <span className="muted"> ({selectedOrg.school_pin_set_at})</span> : null}</>
                        ) : (
                          "Not set"
                        )}
                      </div>

                      <div className="aor-btnrow" style={{ marginTop: 10 }}>
                        <button className="aor-btn" type="button" onClick={() => issueSchoolPin(selectedOrg.id, false)} disabled={loading || deleteBusy || submissionBusy}>Issue School Admin PIN</button>
                        <button className="aor-btn" type="button" onClick={() => issueSchoolPin(selectedOrg.id, true)} disabled={loading || deleteBusy || submissionBusy}>Reissue School Admin PIN</button>
                        <button className="aor-btn danger" type="button" onClick={deleteSelectedOrg} disabled={loading || deleteBusy || submissionBusy} title="Disabled until backend delete route exists.">{deleteBusy ? "Deleting..." : "Delete Organization"}</button>
                      </div>
                    </div>

                    <div className="aor-org-actions">
                      <label className="aor-checkline">
                        <input type="checkbox" checked={truthyActive(selectedOrg.is_active)} onChange={(e) => setOrgActive(selectedOrg.id, e.target.checked)} disabled={loading || deleteBusy || submissionBusy} />
                        <span>Org Active (Approve)</span>
                      </label>

                      <div className="aor-btnrow">
                        <button className="aor-btn" type="button" onClick={() => setAllStudentsActiveForOrg(selectedOrg.id, true)} disabled={loading || deleteBusy || submissionBusy}>Activate ALL Students</button>
                        <button className="aor-btn danger" type="button" onClick={() => setAllStudentsActiveForOrg(selectedOrg.id, false)} disabled={loading || deleteBusy || submissionBusy}>Deactivate ALL Students</button>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div className="aor-panel-title" style={{ marginBottom: 8 }}>School Administrator Contact Info</div>

                    <div className="aor-tablewrap" style={{ padding: 12 }}>
                      <div className="aor-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <input className="aor-input" value={String(contactDraft.contact_name ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, contact_name: e.target.value }))} placeholder="Admin name" />
                        <input className="aor-input" value={String(contactDraft.contact_title ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, contact_title: e.target.value }))} placeholder="Title (Principal, Counselor, etc.)" />
                        <input className="aor-input" value={String(contactDraft.contact_email ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, contact_email: e.target.value }))} placeholder="Email" />
                        <input className="aor-input" value={String(contactDraft.contact_phone ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, contact_phone: e.target.value }))} placeholder="Phone" />
                        <input className="aor-input" value={String(contactDraft.address1 ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, address1: e.target.value }))} placeholder="Address 1" />
                        <input className="aor-input" value={String(contactDraft.address2 ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, address2: e.target.value }))} placeholder="Address 2" />
                        <input className="aor-input" value={String(contactDraft.city ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, city: e.target.value }))} placeholder="City" />
                        <input className="aor-input" value={String(contactDraft.state ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, state: e.target.value }))} placeholder="State" />
                        <input className="aor-input" value={String(contactDraft.zip ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, zip: e.target.value }))} placeholder="ZIP" />

                        <select className="aor-input" value={String(contactDraft.region ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, region: e.target.value, country: "" }))}>
                          <option value="">Select Region</option>
                          {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>

                        <select className="aor-input" value={String(contactDraft.country ?? "")} onChange={(e) => {
                          const nextCountry = e.target.value;
                          const inferredRegion = getRegionForCountry(nextCountry);
                          setContactDraft((p) => ({ ...p, country: nextCountry, region: inferredRegion || p.region || "" }));
                        }}>
                          <option value="">Select Country</option>
                          {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>

                        <input className="aor-input" value={String(contactDraft.district ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, district: e.target.value }))} placeholder="District" />
                        <input className="aor-input" value={String(contactDraft.notes ?? "")} onChange={(e) => setContactDraft((p) => ({ ...p, notes: e.target.value }))} placeholder="Notes" style={{ gridColumn: "1 / -1" }} />
                      </div>

                      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                        <button className="aor-btn" type="button" onClick={saveOrgContact} disabled={contactBusy || loading || deleteBusy || submissionBusy}>{contactBusy ? "Saving..." : "Save Contact Info"}</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div className="aor-panel-title" style={{ marginBottom: 8 }}>
                    Enrollment Submissions
                  </div>

                  <div className="aor-tablewrap">
                    <table className="aor-table">
                      <thead>
                        <tr>
                          <th style={{ width: 110 }}>Submission</th>
                          <th style={{ width: 130 }}>Status</th>
                          <th style={{ width: 100 }}>Rows</th>
                          <th>Submitted</th>
                          <th style={{ width: 120 }}>Review</th>
                          <th style={{ width: 120 }}>PIN Report</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="aor-empty-td">
                              No enrollment submissions found for this organization.
                            </td>
                          </tr>
                        ) : (
                          submissions.map((sub) => {
                            const status = clean(sub.status || "submitted").toLowerCase();
                            const selected = selectedSubmission?.id === sub.id;
                            const pinReady = truthy(sub.pin_report_ready) || status === "approved";

                            return (
                              <tr key={sub.id}>
                                <td>#{sub.id}</td>
                                <td>
                                  <span className={`aor-pill ${status === "approved" ? "ok" : status === "rejected" ? "bad" : ""}`}>
                                    {status === "submitted" ? "Pending" : status || "Pending"}
                                  </span>
                                </td>
                                <td>{sub.row_count ?? 0}</td>
                                <td>{sub.submitted_at || sub.created_at || "—"}</td>
                                <td>
                                  <button
                                    className="aor-btn"
                                    type="button"
                                    onClick={() => viewSubmission(sub)}
                                    disabled={submissionBusy}
                                  >
                                    {selected ? "Selected" : "Review"}
                                  </button>
                                </td>
                                <td>
                                  <button
                                    className="aor-btn"
                                    type="button"
                                    onClick={() => downloadSubmissionCsv(sub)}
                                    disabled={!pinReady || submissionBusy}
                                    title={pinReady ? "Download authorized PIN report" : "Available after approval"}
                                  >
                                    Download
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {selectedSubmission ? (
                    <div className="aor-tablewrap" style={{ marginTop: 12, padding: 12 }}>
                      <div
                        className="aor-panel-title"
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
                      >
                        <span>
                          Review Submission #{selectedSubmission.id} ·{" "}
                          {clean(selectedSubmission.status || "submitted").toLowerCase() === "submitted"
                            ? "Pending"
                            : selectedSubmission.status}
                        </span>

                        <div className="aor-btnrow">
                          <button
                            className="aor-btn"
                            type="button"
                            onClick={approveSubmission}
                            disabled={
                              submissionBusy ||
                              clean(selectedSubmission.status).toLowerCase() === "approved"
                            }
                          >
                            {submissionBusy ? "Working..." : "Approve"}
                          </button>

                          <button
                            className="aor-btn danger"
                            type="button"
                            onClick={rejectSubmission}
                            disabled={
                              submissionBusy ||
                              clean(selectedSubmission.status).toLowerCase() === "rejected"
                            }
                          >
                            Reject
                          </button>
                        </div>
                      </div>

                      <div className="aor-tablewrap" style={{ marginTop: 10 }}>
                        <table className="aor-table">
                          <thead>
                            <tr>
                              <th style={{ width: 90 }}>Row</th>
                              <th style={{ width: 120 }}>Type</th>
                              <th style={{ width: 140 }}>Student ID</th>
                              <th>Name</th>
                              <th style={{ width: 90 }}>Grade</th>
                              <th style={{ width: 110 }}>Role</th>
                              <th>Email</th>
                              <th style={{ width: 150 }}>PIN</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissionRows.length === 0 ? (
                              <tr>
                                <td colSpan={8} className="aor-empty-td">
                                  {submissionBusy ? "Loading submission rows..." : "No rows found for this submission."}
                                </td>
                              </tr>
                            ) : (
                              submissionRows.map((r, index) => {
                                const recordType = clean(r.record_type).toLowerCase();
                                const displayName =
                                  recordType === "student"
                                    ? [r.student_first, r.student_last].map(clean).filter(Boolean).join(" ")
                                    : clean(r.pt_name);
                                const key = issuedKeyFromSubmissionRow(r);
                                const issuedPin = key ? issuedPinsByKey[key] : "";
                                const displayPin = clean(
                                  issuedPin || r.raw_pin || r.pin || r.student_pin || r.pt_pin
                                );

                                return (
                                  <tr key={`${r.row_num ?? index}_${recordType}_${r.student_id || r.pt_code || index}`}>
                                    <td>{r.row_num ?? index + 1}</td>
                                    <td>{recordType === "pt" ? "Parent / Teacher" : "Student"}</td>
                                    <td>{r.student_id || r.pt_code || "—"}</td>
                                    <td>{displayName || "—"}</td>
                                    <td>{r.student_grade || "—"}</td>
                                    <td>{r.pt_role || "—"}</td>
                                    <td>{r.pt_email || "—"}</td>
                                    <td>{displayPin || "To be assigned"}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="aor-students-header">
                  <div className="aor-students-title">Students</div>
                  <input className="aor-input" value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} placeholder="Search student id, name, email, or grade…" />
                </div>
                <div className="aor-tablewrap">
                  <table className="aor-table">
                    <thead><tr><th style={{ width: 110 }}>Active</th><th style={{ width: 180 }}>Student ID</th><th>Name</th><th style={{ width: 100 }}>Grade</th><th style={{ width: 180 }}>PIN</th><th style={{ width: 160 }}>Issue new PIN</th><th style={{ width: 120 }}>Activate</th><th style={{ width: 120 }}>Deactivate</th></tr></thead>
                    <tbody>
                      {filteredStudents.length === 0 ? <tr><td colSpan={8} className="aor-empty-td">No students found for this organization.</td></tr> : filteredStudents.map((s) => {
                        const studentPk = String(s.student_pk ?? s.id);
                        const active = truthyActive(s.is_active);
                        const pinDisplay = formatPinDisplay(issuedPinsByKey[studentIssuedKey(s.student_id)] || s.raw_pin || s.pin || s.student_pin, s.pin_last4, s.pin_set);
                        return (
                          <tr key={`${s.id}_${s.student_id}`}>
                            <td><input type="checkbox" checked={active} onChange={(e) => setStudentActive(studentPk, e.target.checked)} disabled={loading || deleteBusy || submissionBusy} /></td>
                            <td>{s.student_id}</td><td>{s.name || "—"}</td><td>{s.grade || "—"}</td><td>{pinDisplay}</td>
                            <td><button className="aor-btn" type="button" onClick={() => reissueStudentPin(studentPk)} disabled={loading || deleteBusy || submissionBusy}>Reissue</button></td>
                            <td><button className="aor-btn" type="button" onClick={() => setStudentActive(studentPk, true)} disabled={loading || deleteBusy || submissionBusy || active}>Activate</button></td>
                            <td><button className="aor-btn danger" type="button" onClick={() => setStudentActive(studentPk, false)} disabled={loading || deleteBusy || submissionBusy || !active}>Deactivate</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="aor-students-header" style={{ marginTop: 14 }}>
                  <div className="aor-students-title">Parents / Teachers</div>
                  <input className="aor-input" value={ptQuery} onChange={(e) => setPtQuery(e.target.value)} placeholder="Search PT code, name, email, or role…" />
                </div>
                <div className="aor-tablewrap">
                  <table className="aor-table">
                    <thead><tr><th style={{ width: 110 }}>Active</th><th style={{ width: 140 }}>PT Code</th><th style={{ width: 120 }}>Role</th><th>Name</th><th>Email</th><th style={{ width: 180 }}>PIN</th><th style={{ width: 160 }}>Issue new PIN</th><th style={{ width: 120 }}>Activate</th><th style={{ width: 120 }}>Deactivate</th></tr></thead>
                    <tbody>
                      {filteredPts.length === 0 ? <tr><td colSpan={9} className="aor-empty-td">No parent/teacher accounts found for this organization.</td></tr> : filteredPts.map((p) => {
                        const active = truthyActive(p.is_active);
                        const pinDisplay = formatPinDisplay(issuedPinsByKey[ptIssuedKey(p.pt_code)] || p.raw_pin || p.pin || p.pt_pin, p.pin_last4, p.pin_set);
                        return (
                          <tr key={`${p.id}_${p.pt_code}`}>
                            <td><input type="checkbox" checked={active} onChange={(e) => setPtActive(p.id, e.target.checked)} disabled={loading || deleteBusy || submissionBusy} /></td>
                            <td>{p.pt_code}</td><td>{p.pt_role || "—"}</td><td>{p.pt_name || "—"}</td><td>{p.pt_email || "—"}</td><td>{pinDisplay}</td>
                            <td><button className="aor-btn" type="button" onClick={() => reissuePtPin(p.id)} disabled={loading || deleteBusy || submissionBusy}>Reissue</button></td>
                            <td><button className="aor-btn" type="button" onClick={() => setPtActive(p.id, true)} disabled={loading || deleteBusy || submissionBusy || active}>Activate</button></td>
                            <td><button className="aor-btn danger" type="button" onClick={() => setPtActive(p.id, false)} disabled={loading || deleteBusy || submissionBusy || !active}>Deactivate</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
